import {DOMParser} from '@xmldom/xmldom';

import escapeHTML from './escapeHTML';

const DEFAULT_ALLOWED_TAGS: Array<string> = [
  'A',
  'B',
  'BLOCKQUOTE',
  'BR',
  'CAPTION',
  'CODE',
  'DD',
  'DIV',
  'DL',
  'DT',
  'EM',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HR',
  'I',
  'IMG',
  'LI',
  'NL',
  'OL',
  'P',
  'PRE',
  'SPAN',
  'STRIKE',
  'STRONG',
  'TABLE',
  'TBODY',
  'TD',
  'TH',
  'THEAD',
  'TR',
  'UL',
];

const DEFAULT_ALLOWED_ATTRIBUTES: Record<string, Array<string>> = {
  A: ['href', 'title', 'target'],
  IMG: ['alt', 'src', 'title'],
};

const ALLOWED_SCHEMES: Array<string> = ['http://', 'https://', 'mailto:'];

const NON_PRESERVING_TAGS = ['A', 'SPAN'];
const SELF_CLOSING = ['IMG', 'BR', 'HR'];

export default function sanitize(
  markup: string,
  {
    allowedTags = DEFAULT_ALLOWED_TAGS,
    allowedAttributes = DEFAULT_ALLOWED_ATTRIBUTES,
  }: {
    allowedTags?: typeof DEFAULT_ALLOWED_TAGS;
    allowedAttributes?: typeof DEFAULT_ALLOWED_ATTRIBUTES;
  } = {},
): string {
  if (!markup) {
    return '';
  }
  const dp = new DOMParser();
  const doc = dp.parseFromString(
    '<!DOCTYPE html><html><body>' + markup + '</body></html>',
    'text/html',
  );

  const output: Array<string> = [];

  function walk(node: Node, root?: boolean) {
    const nodeName = node.nodeName.toUpperCase();
    switch (node.nodeType) {
      case 3: //Node.TEXT_NODE:
        if (node.nodeValue) {
          output.push(escapeHTML(node.nodeValue));
        }
      case 4: //Node.CDATA_SECTION_NODE:
      case 8: //Node.COMMENT_NODE:
      case 7: //Node.PROCESSING_INSTRUCTION_NODE:
      case 10: //Node.DOCUMENT_TYPE_NODE:
        return;
    }
    const hasTag = !root && node.nodeType === 1; //Node.ELEMENT_NODE;
    const tagAllowed = !hasTag || allowedTags.includes(nodeName);
    if (!tagAllowed) {
      return;
    }

    const elem = node as Element;
    const elemAttributes = Array.from(elem.attributes ?? []);
    const elemAllowedAttributes = allowedAttributes[nodeName] || [];
    const hasAllowedAttributes = elemAttributes.some(attr =>
      elemAllowedAttributes.includes(attr.name.toLowerCase()),
    );
    const shouldPreserveTag =
      hasTag &&
      (!NON_PRESERVING_TAGS.includes(nodeName) || hasAllowedAttributes);
    if (shouldPreserveTag) {
      output.push(`<${node.nodeName.toLowerCase()}`);

      if (hasAllowedAttributes) {
        for (const attr of elemAttributes) {
          const lowerName = attr.name.toLowerCase();
          if (!elemAllowedAttributes.includes(lowerName)) {
            continue;
          }
          if (
            (lowerName === 'src' || lowerName === 'href') &&
            !ALLOWED_SCHEMES.some(scheme =>
              attr.value.toLowerCase().startsWith(scheme),
            )
          ) {
            continue;
          }
          output.push(` ${escapeHTML(lowerName)}="${escapeHTML(attr.value)}"`);
        }
      }

      output.push('>');

      if (SELF_CLOSING.includes(nodeName)) {
        return;
      }
    }

    for (const child of Array.from(node.childNodes)) {
      if (!child) {
        continue;
      }
      walk(child as Node);
    }

    if (shouldPreserveTag) {
      output.push(`</${node.nodeName.toLowerCase()}>`);
    }
  }

  // We step into the BODY element to get the content.
  const elem = doc.lastChild!.lastChild!;
  walk(elem, true);

  return output.join('');
}
